import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import BillingNav from './BillingNav';
import { BillingPage } from './billingUi';
import {
  fetchUsageBilling,
  refreshUsageBilling,
  formatMoney,
  pctUsed,
} from '../../services/billingApi';
import { fetchTenants } from '../../services/licensingApi';

/** Spec §8.4 Usage & Overage */
const BillingUsage = () => {
  const [searchParams] = useSearchParams();
  const [tenantId, setTenantId] = useState(searchParams.get('tenantId') || '');
  const [tenants, setTenants] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [tens, usage] = await Promise.all([
        fetchTenants(),
        fetchUsageBilling({ tenantId: tenantId || undefined }),
      ]);
      setTenants(tens);
      setRows(usage);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = async () => {
    if (!tenantId) {
      toast.error('Select a tenant to refresh usage');
      return;
    }
    try {
      setRows(await refreshUsageBilling(tenantId));
      toast.success('Usage refreshed for current period');
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <BillingPage>
      <BillingNav title="Usage & Overage" />
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Tenant</InputLabel>
          <Select label="Tenant" value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {tenants.map((t) => (
              <MenuItem key={t.tenantId || t.tenant_id} value={String(t.tenantId || t.tenant_id)}>
                {t.tenantName || t.tenant_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleRefresh} disabled={!tenantId}>
          Refresh from license usage
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tenant</TableCell>
              <TableCell>Usage type</TableCell>
              <TableCell>Period</TableCell>
              <TableCell align="right">Included</TableCell>
              <TableCell align="right">Used</TableCell>
              <TableCell align="right">Remaining</TableCell>
              <TableCell align="right">% Used</TableCell>
              <TableCell align="right">Overage</TableCell>
              <TableCell align="right">Overage $</TableCell>
              <TableCell>Billing status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography color="text.secondary">
                    No usage billing rows. Select a tenant and refresh.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => {
                const remaining =
                  Number(r.includedQuantity || 0) - Number(r.actualQuantity || 0);
                return (
                  <TableRow key={r.usageBillingId}>
                    <TableCell>{r.tenantName}</TableCell>
                    <TableCell>{r.usageType}</TableCell>
                    <TableCell>
                      {String(r.periodStart || '').slice(0, 10)} →{' '}
                      {String(r.periodEnd || '').slice(0, 10)}
                    </TableCell>
                    <TableCell align="right">{r.includedQuantity}</TableCell>
                    <TableCell align="right">{r.actualQuantity}</TableCell>
                    <TableCell align="right">{remaining}</TableCell>
                    <TableCell align="right">
                      {pctUsed(r.actualQuantity, r.includedQuantity)}
                    </TableCell>
                    <TableCell align="right">{r.billableQuantity}</TableCell>
                    <TableCell align="right">{formatMoney(r.overageAmount)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.billingStatus} />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      )}
    </BillingPage>
  );
};

export default BillingUsage;
